import instance, { ApiResponse } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { BaseApi } from 'baseApi';
import { endpoints } from 'endpoints.config';
import { Package } from 'pages/newSubscription/models';
import { ISchoolDetails } from 'pages/subscribe/models';
import {
  RenewSubscriptionFormValues,
  SubscriptionsParser,
  UpgradeSubscriptionFormValues,
} from 'pages/subscriptions/models';
import { UpgradeSubscriptionParser } from 'pages/upgradeSubscription/models';
import { setValidPromoCode } from 'reducers/misc';

const UNKOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class SubscriptionsApi extends BaseApi {
  // get subscription list
  public static getSubscriptionsList = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.list
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const list = result.data.details.listSubscriptions;
        const parsedList = list
          ? SubscriptionsParser.parseGetSubscriptionList(
              result.data.details.listSubscriptions
            )
          : null;

        const data = { ...result.data.details, listSubscriptions: parsedList };

        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          //TODO: build error parser
          // const message = UsersParser.parseError(errors);
          // return UsersApi.errorResponse(message);
          return null;
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          //TODO: build error parser
          // const message = UsersParser.parseError(errors);
          // return UsersApi.errorResponse(message);
          return null;
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  // get upgrade subscriptions list
  public static getUpgradeSubscriptionsList = async (
    id: number
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.upgradeSubscriptionsList + `?SubjectId=${id}`
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          //TODO: build error parser
          // const message = UsersParser.parseError(errors);
          // return UsersApi.errorResponse(message);
          return null;
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          //TODO: build error parser
          // const message = UsersParser.parseError(errors);
          // return UsersApi.errorResponse(message);
          return null;
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static upgradeByInvoice = async (
    values: UpgradeSubscriptionFormValues
  ): Promise<any> => {
    try {
      const payload = SubscriptionsParser.buildUpgradeByInvoicePayload(values);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.upgradeByInvoice,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static upgradeByCard = async (
    values: UpgradeSubscriptionFormValues,
    token: any
  ): Promise<any> => {
    try {
      const payload = SubscriptionsParser.buildUpgradeByCardPayload(
        values,
        token
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.upgradeByCard,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateAfter3DSecureUpgrade = async (
    values: any
  ): Promise<any> => {
    try {
      const payload =
        SubscriptionsParser.buildRenewAfter3DSecurePayload(values);
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updatAfter3DSecureUpgrade,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getSchoolInfo = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getSchoolInfo
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateSchoolInfo = async (
    values: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload =
        UpgradeSubscriptionParser.buildSchoolDetailsPayload(values);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updateSchoolInfo,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getSchoolUsers = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getSchoolUsers
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details.schoolUsers;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateSchoolUserAccess = async (
    selectedUsers: string[],
    selectedPackages: Package[]
  ): Promise<any> => {
    try {
      const payload = UpgradeSubscriptionParser.buildSchoolUserAccess(
        selectedUsers,
        selectedPackages
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updateSchoolUserAccess,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getRenewalSubscription = async (id: number): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getRenewalSubscriptions + '?SubjectId=' + id
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static renewByInvoice = async (
    values: RenewSubscriptionFormValues,
    packageIDs: number[],
    expectedGrossAmount: number
  ): Promise<any> => {
    try {
      const payload = SubscriptionsParser.buildRenewByInvoicePayload(
        values,
        packageIDs,
        expectedGrossAmount
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.renewByInvoice,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static renewByCard = async (
    values: RenewSubscriptionFormValues,
    token: any,
    expectedGrossAmount: number,
    packageIDs: number[]
  ): Promise<any> => {
    try {
      const payload = SubscriptionsParser.buildRenewByCardPayload(
        values,
        token,
        expectedGrossAmount,
        packageIDs
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.renewByCard,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static renewAfter3DSecureUpgrade = async (
    values: any
  ): Promise<any> => {
    try {
      const payload =
        SubscriptionsParser.buildRenewAfter3DSecurePayload(values);
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updateAfter3DSecureRenew,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
}
